<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue';
import { storeToRefs } from 'pinia';
import useAggregation from '../../use-aggregation';
import { useCategories } from '@ilteducation/content-ref-data';
import { useDisplay } from 'vuetify';
import FilterOptions from './Filters/FilterOptions.vue';
import type { MultipleFilterOptions, FilterOption, MultipleField, Field, SearchFilter } from '../../search-panel-types';
import FilterRangeAndMultiple from './Filters/FilterRangeAndMultiple.vue';
import FilterMultipleSwitches from './Filters/FilterMultipleSwitches.vue';
import useFormatFilterOptions from '../../composables/use-format-filter-options';
import type { ContentType } from '@ilteducation/content-registry-types';
import type { AggregateCount } from '../../types';
import { useFilters } from '../../composables/use-filters';

type Props = {
  contentType: ContentType;
  filterFields: Field[];
  filterTop?: number;
  currentSubscription: string;
  minAge?: number;
  maxAge?: number;
  allowedSubscriptionFilterKeys?: string[];
  height?: string;
  defaultOpenFilters?: string[];
};
const props = withDefaults(defineProps<Props>(), {
  allowedSubscriptionFilterKeys: () => [],
  defaultOpenFilters: () => [],
});

const aggregateStore = useAggregation(props.contentType);
const { result, isLoading } = storeToRefs(aggregateStore);
const { load: loadCategories } = useCategories();

const { mdAndUp } = useDisplay();
const { transformToFilterOptions } = useFormatFilterOptions();

const { selectedFilters, filtersToQuery, filterVisibleFields, getAgeFilterItems } = useFilters();

const visibleFields = computed(() => filterVisibleFields(props.filterFields));

// Defines which expand panels are opened.
const expansionPanel = ref<number[]>([]);
let defaultsApplied = false;

watch(
  visibleFields,
  (currentFields) => {
    if (defaultsApplied || currentFields.length === 0 || props.defaultOpenFilters.length === 0) {
      return;
    }
    defaultsApplied = true;
    expansionPanel.value = currentFields.reduce(
      (open: number[], field, index) =>
        props.defaultOpenFilters.includes(field.aggregationKey) ? [...open, index] : open,
      [],
    );
  },
  { immediate: true },
);

const filterContainer = ref(null);

const topFilter = computed(() => props.filterTop || 0);

const heightValue = computed(() => {
  if (props.height) {
    return props.height;
  }
  return mdAndUp.value ? `calc(100vh - ${topFilter.value}px - env(safe-area-inset-bottom))` : 'auto';
});

const getCategories = async (): Promise<void> => {
  await loadCategories(props.currentSubscription);
};

onMounted(async () => {
  await getCategories();
});

const getFilterOptions = (field: Field): FilterOption[] => {
  const currentField = result.value.find((i) => i.field === field.aggregationKey);
  if (!currentField) {
    return [];
  }
  if (field.aggregationKey === 'ages' && typeof props.minAge !== 'undefined' && typeof props.maxAge !== 'undefined') {
    return transformToFilterOptions(field, getAgeFilterItems(props.minAge, props.maxAge));
  }
  if (field.aggregationKey === 'accessRules.subscriptionType' && props.allowedSubscriptionFilterKeys?.length) {
    return transformToFilterOptions(
      field,
      currentField.counts.filter((item: AggregateCount) => props.allowedSubscriptionFilterKeys.includes(item.key)),
    );
  }

  return transformToFilterOptions(field, currentField.counts);
};

const getGroupedFilterOptions = (field: MultipleField): MultipleFilterOptions[] =>
  field.fields.reduce(
    (options: MultipleFilterOptions[], singleField) => [
      ...options,
      {
        field: singleField,
        options: getFilterOptions(singleField),
      },
    ],
    [] as MultipleFilterOptions[],
  );
</script>

<template>
  <div ref="filterContainer" class="filters-container" :style="`top: ${topFilter}px;`">
    <v-expansion-panels v-if="result.length > 0" v-model="expansionPanel" multiple>
      <template v-for="(field, index) in visibleFields">
        <FilterMultipleSwitches
          v-if="field.type === 'multiple_switches'"
          :key="field.label"
          :filters="getGroupedFilterOptions(field as MultipleField)"
          :label="field.label"
          :selected="selectedFilters"
          @filters-updated="(params: SearchFilter) => filtersToQuery(params)"
          :data-testid="`filter-${index}`"
        />
        <FilterRangeAndMultiple
          v-else-if="field.type === 'multiple_predefined_ranges'"
          :key="'multiple_predefined_ranges' + index"
          :items="getFilterOptions(field)"
          :field-key="field.aggregationKey"
          :label="field.label"
          :ranges="field.ranges ?? []"
          :selected="selectedFilters"
          :value-label-mappings="field.valueLabelMappings"
          @filters-updated="(params: SearchFilter) => filtersToQuery(params)"
          :data-testid="`filter-${index}`"
        />
        <FilterOptions
          v-else-if="field.type === 'list'"
          :key="'options' + index"
          :field-key="field.aggregationKey"
          :items="getFilterOptions(field)"
          :selected="selectedFilters"
          :label="field.label"
          @filters-updated="(params: SearchFilter) => filtersToQuery(params)"
          :data-testid="`filter-${index}`"
        />
      </template>
      <v-overlay class="h-100" :opacity="0.2" :value="isLoading || result.length === 0" position="absolute">
        <v-progress-circular :size="70" :width="7" color="primary" indeterminate></v-progress-circular>
      </v-overlay>
    </v-expansion-panels>
  </div>
</template>

<style scoped lang="scss">
@use 'sass:map';
@use 'vuetify/settings' as v;

.v-expansion-panels {
  justify-content: flex-start;
}
.filters-container {
  position: sticky;
  overflow-y: auto;
  width: 100%;
  top: v-bind(topFilter);
  height: v-bind(heightValue);
  @media #{map.get(v.$display-breakpoints, 'md-and-up')} {
    overscroll-behavior: contain;
    scrollbar-gutter: stable;
  }
}
@media #{map.get(v.$display-breakpoints, 'md-and-up')} {
  /* Chrome, Safari and Opera */
  .filters-container::-webkit-scrollbar {
    display: none;
  }

  .filters-container::-webkit-scrollbar-track {
    scrollbar-width: thin; /* Firefox */
    background-color: rgb(var(--v-theme-surface-variant));
  }

  .filters-container::-webkit-scrollbar-thumb {
    border-radius: 8px;
    background-color: rgba(0, 0, 0, 0.3);
    -webkit-box-shadow: 0 0 1px rgba(255, 255, 255, 0.5);
  }

  .filters-container::-webkit-scrollbar-thumb:hover {
    background-color: rgba(0, 0, 0, 0.4);
  }
}
</style>
